import { AbilityBuilder, createMongoAbility } from '@casl/ability'

import type { UserDataType } from '~/auth/AuthProvider'

export type Actions = 'manage' | 'create' | 'read' | 'update' | 'delete'
export type ACLObj = { action: Actions; subject: string }

export const buildAbilityFor = (user: UserDataType) => {
  const { can, cannot, build } = new AbilityBuilder(createMongoAbility)

  // Give everyone manage-all privileges. `manage` and `all` are special keywords in CASL.
  // `manage` represents any action and `all` represents any subject.
  can('manage', 'all')

  // Permissions shared between all non-admin roles
  const sharedPermissions = () => {
    cannot('manage', 'admin')

    if (user.sites && user.sites.length > 0) {
      cannot('read', 'charger')
      cannot('read', 'site')
      cannot('read', 'vehicle')

      can('read', 'charger', { depotId: { $in: user.sites } })
      can('read', 'site', { depotId: { $in: user.sites } })
      can('read', 'vehicle', { homeSiteId: { $in: user.sites } })
    }

    cannot('update', 'reservation')
    can('update', 'reservation', { createdBy: { $eq: user.id } })

    cannot('create', 'charger')
    cannot('update', 'charger')

    cannot('create', 'site')
    cannot('update', 'site')

    cannot('create', 'vehicle')
    cannot('update', 'vehicle')

    cannot('create', 'loi')
    cannot('update', 'loi')

    cannot('create', 'alert')
    cannot('update', 'alert')

    cannot('create', 'site_event')
    cannot('update', 'site_event')

    cannot('create', 'charger_event')
    cannot('update', 'charger_event')

    cannot('create', 'trips')
    cannot('update', 'trips')

    cannot('create', 'fleet')
    cannot('update', 'fleet')

    cannot('create', 'user_group')
    cannot('update', 'user_group')
    cannot('read', 'user_group')

    // Admin settings visibility
    cannot('read', 'admin_settings')
    cannot('read', 'user_settings')

    // Used with user roles
    cannot('update', 'users')
    cannot('create', 'admin_users')
    cannot('create', 'operator_users')
    cannot('create', 'driver_users')

    cannot('read', 'ocpp_logs')

    cannot('read', 'product')
    cannot('create', 'product')
    cannot('update', 'product')

    cannot('read', 'agreement')
    cannot('create', 'agreement')
    cannot('update', 'agreement')

    cannot('read', 'custom_tariff')
    cannot('create', 'custom_tariff')
    cannot('update', 'custom_tariff')

    cannot('read', 'integrations')

    cannot('create', 'connected_device')
    cannot('update', 'connected_device')
    cannot('delete', 'connected_device')

    cannot('manage', 'charger_reboot')

    // Operations (projects / issues): non-admins can read but not mutate by default.
    cannot('create', 'project')
    cannot('update', 'project')
    cannot('delete', 'project')
    cannot('create', 'issue')
    cannot('update', 'issue')
    cannot('delete', 'issue')
  }

  if (user.role !== 'admin') {
    sharedPermissions()
  }

  // Access level: read-only users keep their role's visibility (read abilities and the
  // `manage`/`admin` checks that drive page access) but lose every mutating action —
  // both org-data writes and commands sent to physical assets. Self-service account
  // settings are exempt; those flows do not consult the ability. Applied after the
  // role blocks below so it overrides any role-specific re-grants.
  const readOnlyPermissions = () => {
    cannot('create', 'all')
    cannot('update', 'all')
    cannot('delete', 'all')
    // The site edit page gates on the nonstandard 'edit' action, which the denials
    // above don't cover.
    cannot('edit', 'all')

    // `manage`-keyed write subjects: a `can('manage', subject)` query only matches
    // rules keyed 'manage', so the action denials above don't block them.
    cannot('manage', 'charger_reboot')
    cannot('manage', 'charger_transaction')
    cannot('manage', 'charger_configuration')
    cannot('manage', 'custom_tariff')
  }

  // Additionally modify role-based abilities
  if (user.role === 'fleet operator') {
    can('create', 'charger_event')
    can('update', 'charger_event')

    can('update', 'reservation')

    can('read', 'ocpp_logs')

    can('create', 'trips')
    can('update', 'trips')

    can('create', 'vehicle')
    can('update', 'vehicle')

    can('read', 'admin_settings')
    can('read', 'user_settings')

    can('create', 'driver_users')

    can('update', 'users')

    can('manage', 'charger_reboot')

    // Fleet operators manage operations projects/issues (but not access grants).
    can('create', 'project')
    can('update', 'project')
    can('create', 'issue')
    can('update', 'issue')
    can('delete', 'issue')
  }

  if (user.readOnly) {
    readOnlyPermissions()
  }

  return build()
}

export const defaultACLObj: ACLObj = {
  action: 'manage',
  subject: 'all'
}
